import React, { useEffect, useRef, useState } from 'react';
import {
  DEFAULT_TIME_TABLE,
  EMPLOYEE_BASED_OPTION,
  WORK_TIME_OPTIONS,
} from 'shared/consts/schedule';
import { DropdownWithSelect, Switcher } from 'shared/ui';
import {
  daysOfWeekEng,
  ITimeTableInputProps,
  IWeekWorkTime,
} from '../interfaces/time-table-input.interface';
import { EveryDayInput } from './every-day-input/every-day-input';
import { WeekTimeSlots } from './week-time-slots/week-time-slots';
import styles from './working-hours-picker.module.scss';

export const WorkingHoursPicker: React.FC<ITimeTableInputProps> = ({
  inputTitle = 'Заголовок',
  onChangeTimeTableHandler,
  inputTimeTable,
  showEmployeeOption = false,
  isWorkTimeByEmployee = false,
  onSwitchChange,
}) => {
  function checkIfAllTimeTablesAreEqual(TimeTable: IWeekWorkTime): boolean {
    let counter = 0;
    daysOfWeekEng.map((day, index, days) => {
      if (index > 0) {
        if (
          TimeTable[day].start === TimeTable[days[0]].start &&
          TimeTable[day].end === TimeTable[days[0]].end
        ) {
          counter++;
        }
      }
    });
    return counter === 6;
  }

  const displayOptions = showEmployeeOption
    ? [EMPLOYEE_BASED_OPTION, ...WORK_TIME_OPTIONS]
    : WORK_TIME_OPTIONS;

  const [selectedOption, setSelectedOption] = useState<string>(
    WORK_TIME_OPTIONS[1]
  );

  const [isSwitchActive, setIsSwitchActive] = useState(false);

  const syncedFromData = useRef(false);

  useEffect(() => {
    if (syncedFromData.current) return;
    if (isWorkTimeByEmployee === undefined) return;

    const isConsistent = isWorkTimeByEmployee
      ? inputTimeTable === null
      : inputTimeTable !== null;
    if (!isConsistent) return;

    syncedFromData.current = true;
    setIsSwitchActive(isWorkTimeByEmployee);

    if (isWorkTimeByEmployee) {
      setSelectedOption(EMPLOYEE_BASED_OPTION);
    } else if (inputTimeTable) {
      setSelectedOption(
        checkIfAllTimeTablesAreEqual(inputTimeTable)
          ? WORK_TIME_OPTIONS[0]
          : WORK_TIME_OPTIONS[1]
      );
    } else {
      setSelectedOption(WORK_TIME_OPTIONS[1]);
    }
  }, [isWorkTimeByEmployee, inputTimeTable]);

  const handleOptionChange = (option: string) => {
    setSelectedOption(option);
    if (option === EMPLOYEE_BASED_OPTION) {
      onChangeTimeTableHandler(null);
      setIsSwitchActive(false);
      onSwitchChange?.(false);
    } else if (inputTimeTable === null) {
      onChangeTimeTableHandler(DEFAULT_TIME_TABLE);
    }
  };

  const handleSwitchClick = () => {
    const newValue = !isSwitchActive;
    setIsSwitchActive(newValue);
    onSwitchChange?.(newValue);
  };

  return (
    <div className={styles.input}>
      <div className={styles.input__title}>{inputTitle}</div>
      <div className={styles.input__content}>
        <DropdownWithSelect
          options={displayOptions}
          selected={selectedOption.valueOf()}
          onChange={handleOptionChange}
        />
        {selectedOption === EMPLOYEE_BASED_OPTION && showEmployeeOption && (
          <div className={styles.employee_mode}>
            <p className={styles.employee_mode__info}>
              График будет формироваться на основе графика работы сотрудников.
              Чтобы видеть график точки на неделю, необходимо настроить график
              сотрудников на неделю.
            </p>
            <div
              className={styles.employee_mode__switch_wrapper}
              onClick={handleSwitchClick}
            >
              <Switcher isActive={isSwitchActive} onClick={handleSwitchClick} />
              <span className={styles.employee_mode__label}>Активно</span>
            </div>
          </div>
        )}
        {selectedOption === WORK_TIME_OPTIONS[0] && inputTimeTable != null && (
          <EveryDayInput
            onChangeHandler={onChangeTimeTableHandler}
            newDaySlot={inputTimeTable}
          />
        )}
        {selectedOption === WORK_TIME_OPTIONS[1] && inputTimeTable != null && (
          <WeekTimeSlots
            onChangeHandler={onChangeTimeTableHandler}
            newTimeTable={inputTimeTable}
          />
        )}
      </div>
    </div>
  );
};
